import { isDefined } from "@clipboard-health/util-ts";
import { v4 as uuidv4 } from "uuid";

import { PROFESSIONAL_REFERENCE_STATUS } from "../constants";
import type {
  ProfessionalReference,
  ProfessionalReferenceStatus,
  ReferenceFormData,
} from "../types";

export function generateReferenceFormId() {
  return `reference-form:${uuidv4()}` as const;
}

export function getReferenceFormFieldId(formId: string, fieldName: keyof ReferenceFormData) {
  return `${formId}:field:${fieldName}` as const;
}

export function getReferenceFormFieldErrorId(fieldId: string) {
  return `${fieldId}:error`;
}

interface CalculateInitialFormIdsParams {
  references: ProfessionalReference[];
  minVerifiedReferenceRequired: number;
  completed: boolean;
}

/**
 * Calculate the count of references grouped by status
 */
export function getReferencesCountsByStatus(
  references?: ProfessionalReference[]
): Record<ProfessionalReferenceStatus, number> {
  const initialCounts: Record<ProfessionalReferenceStatus, number> = {
    [PROFESSIONAL_REFERENCE_STATUS.REQUESTED]: 0,
    [PROFESSIONAL_REFERENCE_STATUS.DECLINED]: 0,
    [PROFESSIONAL_REFERENCE_STATUS.VERIFIED]: 0,
  };

  if (!isDefined(references)) {
    return initialCounts;
  }

  return references.reduce<Record<ProfessionalReferenceStatus, number>>(
    (accumulator, reference) => {
      accumulator[reference.status] = (accumulator[reference.status] ?? 0) + 1;
      return accumulator;
    },
    initialCounts
  );
}

/**
 * Calculate initial form ids based on current reference state
 * Matches the old implementation logic for determining how many forms to show
 */
export function calculateInitialFormIds(
  params: CalculateInitialFormIdsParams
): Array<{ id: string }> {
  const { references, minVerifiedReferenceRequired, completed } = params;

  // Don't show forms if workflow is completed
  if (completed) {
    return [];
  }

  const {
    [PROFESSIONAL_REFERENCE_STATUS.REQUESTED]: requestedCount,
    [PROFESSIONAL_REFERENCE_STATUS.VERIFIED]: verifiedCount,
    [PROFESSIONAL_REFERENCE_STATUS.DECLINED]: declinedCount,
  } = getReferencesCountsByStatus(references);

  const hasRejectedMoreThanRequested = declinedCount > requestedCount;
  const verifiedLessThanRequired = verifiedCount < minVerifiedReferenceRequired;
  const notEnoughRequestedOrVerified =
    requestedCount + verifiedCount < minVerifiedReferenceRequired;
  const hasEnoughReferences = references.length >= minVerifiedReferenceRequired;

  // Show exactly 1 form when there are declined references
  if (
    (hasEnoughReferences && hasRejectedMoreThanRequested && verifiedLessThanRequired) ||
    (declinedCount > 0 && notEnoughRequestedOrVerified)
  ) {
    return [{ id: generateReferenceFormId() }];
  }

  // Show forms based on total reference count
  if (references.length < minVerifiedReferenceRequired) {
    const formsNeeded = minVerifiedReferenceRequired - references.length;
    return Array.from({ length: formsNeeded }, () => ({ id: generateReferenceFormId() }));
  }

  return [];
}
